import React from 'react'
import Typography from '../../Typography'
import { useSelector } from 'react-redux';
import { StoreState } from '../../../../../store/reducer-main';
import ModifyBlock from '../ModifyBlock';
import theme from '../../../../theme';
import InfoBox from '../InfoBox';
import { adjustBillingCicleName, adjustBillingName, adjustSubscriptionStatusCileName, formatDate } from '../../../../../helpers/utils';
import { formaTimestampTo_DD_MM_YY } from '../../../../../helpers/date';
import Button from '../../Button';
import { MembershipType } from '../../../../../enums/profile';
import { getStripePaymentErrorDescription, processCardPayment, retryStripePayment } from '../../../../../service/common/stripe';
import { useStripe } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import CustomModal from '../../CustomModal';
import ReducedProductCard from '../../ReducedProductCard';
import { getPlanRecurringPeriod } from '../../../../../helpers/stripeUtils';
import { ProductWithPrice } from '../../../../../types/stripe';

interface Props{
  onSubscriptionModifyPress:()=>void;
  onInterruptSubscription: () => void;
  onPurchasePlan: (priceId: string) => void;
  interruptLoading: boolean;
  purchaseLoading: boolean;
}

export default function SubscriptionSection({
  onSubscriptionModifyPress,
  onInterruptSubscription,
  onPurchasePlan,
  interruptLoading,
  purchaseLoading,
}:Props) {

  const { userProfile, paymentMethod } = useSelector((state: StoreState) => state.profile);
  const {products, stripeInstance} = useSelector((state: StoreState) => state.stripe);  
  
  const [modalOpen, setModaleOpen] = React.useState<boolean>(false);
  const [selecedProd, setSelectedProd] = React.useState<ProductWithPrice | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const subscriptionErrorDescription = React.useMemo(() => {
    if (!userProfile) return null;
    if (!userProfile.membership) return null;
    if (userProfile.membership.type === MembershipType.TRIAL) return null;
    if (!userProfile.membership.error) return null;
    return getStripePaymentErrorDescription(userProfile.membership.error.code);
  }, [userProfile]);

  const name = React.useMemo(() => {
    const membershipData = userProfile?.membership;
    if (!membershipData) return null;
    if (membershipData.type === MembershipType.TRIAL) return 'PROMO' ;
    const idx = products.findIndex(el => el.id === membershipData.stripeProductRef);
    return idx < 0 ? null : products[idx].name;
  }, [products, userProfile]);

  const billingCicle = React.useMemo(() => {
    const membershipData = userProfile?.membership;
    if (!membershipData) return null;
    if (membershipData.type === MembershipType.TRIAL) return 'Nessuno';
    const idx = products.findIndex(el => el.id === membershipData.stripeProductRef);
    const interval =  idx < 0 ? null : products[idx].default_price.recurring?.interval;
    const interval_count = idx < 0 ? null : products[idx].default_price.recurring?.interval_count;
    if (interval === 'year') return 'Annuale';
    else if (interval === 'month') {
      if (interval_count === 1) return 'Mensile';
      else return 'Semestrale';
    }
    return 'Semestrale';
  }, [products, userProfile]);
  
  const renewalDate = React.useMemo(() => {
    const membershipData = userProfile?.membership;
    if (!membershipData) return null;
    const planEndDate = membershipData.type === MembershipType.SUBSCRIPTION
      ? membershipData.subscriptionData.current_period_end * 1000
      : membershipData.tsEnd;
    const date = new Date(planEndDate);
    return formatDate(date);
  }, [userProfile]);

  const isActive = React.useMemo(() => {
    const membershipData = userProfile?.membership;
    if (!membershipData) return false;
    return  membershipData.type === MembershipType.SUBSCRIPTION
    ? membershipData.subscriptionData?.status === 'active'
    : membershipData.tsEnd>Date.now();
  }, [userProfile]);

  const nextBillingCycleProd = React.useMemo(() => {
    if (!userProfile) return null;
    if (!userProfile.membership || userProfile.membership.type === MembershipType.TRIAL) return null;
    if (!userProfile.membership.scheduledUpdate) return null;
    const nextPriceId = userProfile.membership.scheduledUpdate.newPriceId;
    const correspondingProd = products.find(el => el.default_price.id === nextPriceId);
    return correspondingProd ? correspondingProd.name : null;
  }, [userProfile, products]);

  const subscriptionIsInterrupted = React.useMemo(() => {
    if (!userProfile) return false;
    if (!userProfile.membership || userProfile.membership.type === MembershipType.TRIAL) return false;
    return !!userProfile.membership.subscriptionData?.cancel_at_period_end
  }, [userProfile]);

  const nextRenewalDateLabel = React.useMemo(() => {
    if (userProfile?.membership?.type === MembershipType.TRIAL) return 'Data fine periodo di prova';
    return 'Data prossimo rinnovo';
  }, [userProfile]);

  const isBillingDataPresent = React.useMemo(() => {
    if (!userProfile) return false;
    if (!userProfile.stripeCustomer.metadata.name) return false;
    if (!userProfile.stripeCustomer.metadata.surname) return false;
    if (!userProfile.stripeCustomer.metadata.postalCode) return false;
    if (!userProfile.stripeCustomer.metadata.address) return false;
    if (!userProfile.stripeCustomer.metadata.companyName) return false;
    if (!userProfile.stripeCustomer.metadata.vatNumber) return false;
    if (!userProfile.stripeCustomer.metadata.sdi) return false;
    return true;
  }, [userProfile]);

  const onRetryPayment = async () => {
    try {
      setLoading(true);
      const cfRes = await retryStripePayment();
      const client_secret = cfRes.client_secret;
      console.log({client_secret});
      if (!stripeInstance || !client_secret) {
        setLoading(false);
        return;
      }
      const { error, paymentIntent } = await processCardPayment(stripeInstance, client_secret); 
      if (error) {
        console.log('ERRORE:', error);
        toast.error('Si è verificato un errore durante il pagamento');
        setLoading(false);
      } else {
        console.log('SUCCESS:', paymentIntent);
        toast.success('Pagamento confermato');
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (error) {
      console.log('RETRY PAYMENT ERROR', error);
    }
  }

  const onPurchase = (prod: ProductWithPrice) => {
    onPurchasePlan(prod.default_price.id);
  }

  return (
    <div className='flex justify-between items-center my-5'>
      <div>
        <div  className='flex items-center mb-2'>
        <Typography variant='display'>
        ABBONAMENTO
        </Typography>
        <ModifyBlock onClick={onSubscriptionModifyPress} style={{marginLeft:theme.sizing.x1}} modifyItem='Piano'/>
        </div>
        <div className='flex gap-14 items-end'>
          <InfoBox title='Nome' content={name??""}/>
          <InfoBox title='Ciclo Fatturazione' content={billingCicle??""}/>
          <InfoBox title={nextRenewalDateLabel} content={`${renewalDate}${subscriptionIsInterrupted ? ' - Interrotta' : nextBillingCycleProd ? ' - ' + nextBillingCycleProd : ''}`}/>
          <InfoBox contentColor={isActive?"success":"error"} 
            title='Stato Abbonamento' 
            content={isActive?"Attivo":"Terminato"}/>
            {
              isActive && userProfile?.membership?.type !== MembershipType.TRIAL && 
              <Button 
                loading={interruptLoading}
                disabled={interruptLoading || subscriptionIsInterrupted}
                variant='secondary'
                colorVariant='error' 
                onClick={onInterruptSubscription} 
                title='INTERROMPI RINNOVO'/>
            }
            {
              !isActive&& isBillingDataPresent && paymentMethod &&
              <Button 
                loading={purchaseLoading}
                disabled={purchaseLoading}
                variant='secondary'
                colorVariant='success' 
                onClick={() => setModaleOpen(true)} 
                title='AQUISTA PIANO'/>
            }
        </div>
        {
          subscriptionErrorDescription&&
          <div 
            className='p-5 my-5' 
            style={{backgroundColor:theme.color.error+"50",borderRadius: theme.sizing.x2}}>
            <Typography variant="button" style={{fontWeight: '700'}}>Si è verificato un errore durante il pagamento</Typography>
            <Typography variant="button">{subscriptionErrorDescription}</Typography>
            <Button
              disabled={loading}
              loading={loading}
              className='mt-4' 
              variant='secondary'
              colorVariant="primary" 
              onClick={onRetryPayment} 
              title='RITENTA RINNOVO'/>
          </div>
        }
      </div>
      <CustomModal 
        isOpen={modalOpen}
        onOpenChange={setModaleOpen}
        content={
        <div className='my-4'>
          <Typography style={{textAlign:"center"}} variant="display">Acquista Piano</Typography>
          <div className='flex flex-col justify-center my-4 gap-4'>
            {products.map((prod)=>{
              return (
                <ReducedProductCard
                  reduced
                  key={prod.id}
                  productName={prod.name}
                  onPress={() => setSelectedProd(prod)}
                  productDescription={prod.description}
                  recurringPeriod={getPlanRecurringPeriod(prod.default_price)}
                  price={`${((prod.default_price.unit_amount ?? 0) / 100).toFixed(2).replace('.', ',')} €`}
                  selected={prod.id === selecedProd?.id}
                  />
              )
            })}
            <Button className='mt-8' onClick={selecedProd ? () => onPurchase(selecedProd) : () => {}} colorVariant="primary" title='ACQUISTA' variant="primary" loading={purchaseLoading} disabled={purchaseLoading || !selecedProd}/>
          </div>
        </div>
      } />
   </div>
  )
}
